import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Briefcase, Handshake } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { useI18n } from "../i18n/I18nContext";
import ActivitySelect from "../components/ActivitySelect";

const STATUS_STYLES = {
  nuova: "bg-gold/20 text-midnight border-gold/40",
  in_lavorazione: "bg-blue-100 text-blue-800 border-blue-200",
  completata: "bg-green-100 text-green-800 border-green-200",
  chiusa: "bg-gray-100 text-gray-600 border-gray-200",
};
const STATUS_KEYS = ["nuova", "in_lavorazione", "completata", "chiusa"];

function StatusTimeline({ status, steps }) {
  const activeIdx = STATUS_KEYS.indexOf(status);
  return (
    <div className="mt-4 flex items-center" data-testid="status-timeline">
      {steps.map((label, i) => {
        const done = i <= activeIdx;
        return (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-3.5 h-3.5 rounded-full border-2 transition-colors ${done ? "bg-gold border-gold" : "bg-transparent border-sand"}`} />
              <span className={`mt-1.5 text-[11px] whitespace-nowrap ${done ? "text-midnight font-medium" : "text-muted-foreground"}`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 -mt-5 ${i < activeIdx ? "bg-gold" : "bg-sand"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Requests() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "specific_job", title: "", description: "", timeline: "", activity_code: "", division_code: "" });

  const load = () => api.get("/requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const typeLabels = { specific_job: t("requests.typeSpecificFull"), partnership: t("requests.typePartnershipFull") };
  const steps = t("requests.steps");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/requests", form);
      toast.success(t("requests.sent"));
      setOpen(false);
      setForm({ type: "specific_job", title: "", description: "", timeline: "", activity_code: "", division_code: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl" data-testid="requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-gold">{t("requests.overline")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("requests.title")}</h1>
          <p className="text-muted-foreground mt-2">{t("requests.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-request-btn" className="rounded-none bg-gold text-midnight hover:brightness-105 transition-all duration-300">
              <Plus className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("requests.newRequest")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-white border-sand max-w-lg" data-testid="new-request-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-midnight">{t("requests.dialogTitle")}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">{t("requests.subtitle")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label className="text-midnight text-sm mb-2 block">{t("requests.typeLabel")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: "specific_job", label: t("requests.typeSpecific"), icon: Briefcase },
                    { v: "partnership", label: t("requests.typePartnership"), icon: Handshake },
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setForm({ ...form, type: o.v })}
                      data-testid={`request-type-${o.v}`}
                      className={`flex flex-col items-start gap-2 p-4 border text-start transition-all duration-200 ${
                        form.type === o.v ? "border-gold bg-gold/10" : "border-sand hover:border-midnight"
                      }`}>
                      <o.icon className="h-5 w-5 text-midnight" strokeWidth={1.5} />
                      <span className="text-sm text-midnight">{o.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="title" className="text-midnight text-sm">{t("requests.titleLabel")}</Label>
                <Input id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="request-title-input" className="mt-1.5 rounded-none border-sand focus-visible:ring-gold" />
              </div>
              <div>
                <Label htmlFor="desc" className="text-midnight text-sm">{t("requests.descLabel")}</Label>
                <Textarea id="desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="request-description-input" placeholder={t("requests.descPh")}
                  className="mt-1.5 rounded-none border-sand focus-visible:ring-gold min-h-[110px]" />
              </div>
              <div>
                <Label htmlFor="timeline" className="text-midnight text-sm">{t("requests.timelineLabel")}</Label>
                <Input id="timeline" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })}
                  data-testid="request-timeline-input" placeholder={t("requests.timelinePh")}
                  className="mt-1.5 rounded-none border-sand focus-visible:ring-gold" />
              </div>
              <ActivitySelect value={form.activity_code} onChange={(v) => setForm({ ...form, activity_code: v, division_code: "" })}
                divisionValue={form.division_code} onDivisionChange={(v) => setForm({ ...form, division_code: v })}
                testId="request-activity-select" divisionTestId="request-division-select" />
              <Button type="submit" disabled={saving} data-testid="submit-request-btn"
                className="w-full h-11 rounded-none bg-midnight text-bone hover:bg-midnight/90 transition-all duration-300">
                {saving ? t("requests.submitting") : t("requests.submit")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-sand bg-white p-14 text-center" data-testid="requests-empty">
          <Briefcase className="h-8 w-8 text-muted-foreground mx-auto" strokeWidth={1.5} />
          <p className="text-muted-foreground mt-4">{t("requests.empty")}</p>
        </div>
      ) : (
        <div className="border border-sand bg-white divide-y divide-sand" data-testid="requests-list">
          {items.map((r) => (
            <div key={r.request_id} className="p-6 hover:bg-muted/40 transition-colors" data-testid={`request-row-${r.request_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="rounded-none border-sand text-xs">{typeLabels[r.type]}</Badge>
                    <Badge variant="outline" className={`rounded-none text-xs ${STATUS_STYLES[r.status] || ""}`}>
                      {t(`requests.status.${r.status}`)}
                    </Badge>
                  </div>
                  <h3 className="font-serif text-lg text-midnight mt-3">{r.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.description}</p>
                  {r.timeline && <p className="text-xs text-muted-foreground mt-2">{t("requests.timelinePrefix")} {r.timeline}</p>}
                  {r.admin_notes && (
                    <div className="mt-3 border-s-2 border-gold bg-gold/5 px-4 py-2" data-testid={`request-admin-note-${r.request_id}`}>
                      <p className="overline text-gold text-[10px]">{t("requests.adminNote")}</p>
                      <p className="text-sm text-midnight mt-0.5">{r.admin_notes}</p>
                    </div>
                  )}
                  <StatusTimeline status={r.status} steps={steps} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
